import {
  AlertTriangle,
  CheckCircle2,
  CircleAlert,
  Info,
  XCircle,
} from 'lucide-react';

export type HelperBannerProps = {
  variant: 'info' | 'success' | 'warning' | 'error' | 'neutral';
  title: string;
  message: string;
  actionButton?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary';
  };
  cancelButton?: {
    label: string;
    onClick: () => void;
  };
};

const HelperBanner = ({
  variant,
  title,
  message,
  actionButton,
  cancelButton,
}: HelperBannerProps) => {
  const variantStyles = {
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      title: 'text-blue-700',
      message: 'text-blue-600',
      icon: 'text-blue-600',
    },
    success: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      title: 'text-emerald-800',
      message: 'text-emerald-700',
      icon: 'text-emerald-600',
    },
    warning: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      title: 'text-amber-800',
      message: 'text-amber-700',
      icon: 'text-amber-600',
    },
    error: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      title: 'text-red-800',
      message: 'text-red-700',
      icon: 'text-red-600',
    },
    neutral: {
      bg: 'bg-gray-50',
      border: 'border-gray-200',
      title: 'text-gray-800',
      message: 'text-gray-700',
      icon: 'text-gray-600',
    },
  };

  const styles = variantStyles[variant];

  const getIcon = () => {
    const baseClass = `w-5 h-5 ${styles.icon} mt-0.5 flex-shrink-0`;
    switch (variant) {
      case 'info':
        return <Info className={baseClass} />;
      case 'success':
        return <CheckCircle2 className={baseClass} />;
      case 'warning':
        return <AlertTriangle className={baseClass} />;
      case 'error':
        return <XCircle className={baseClass} />;
      case 'neutral':
        return <CircleAlert className={baseClass} />;
    }
  };

  return (
    <div
      className={`mb-2 p-2 ${styles.bg} border ${styles.border} rounded-lg flex flex-col md:flex-row md:items-center md:justify-between gap-2`}>
      <div className="flex items-start gap-2 sm:gap-3">
        {getIcon()}
        <div className="flex-1 min-w-0">
          <p
            className={`text-sm sm:text-base font-semibold ${styles.title} mb-1`}>
            {title}
          </p>
          <p className={`text-xs sm:text-sm ${styles.message}`}>
            {message}
          </p>
        </div>
      </div>
      {(actionButton || cancelButton) && (
        <div className="flex flex-col md:flex-row gap-2 md:ml-4 md:flex-shrink-0 w-full md:w-auto">
          {actionButton && (
            <button
              onClick={actionButton.onClick}
              className={`w-full sm:w-auto px-3 sm:px-4 py-2.5 sm:py-2 text-sm font-semibold rounded-lg border min-h-[44px] sm:min-h-0 ${
                actionButton.variant === 'primary'
                  ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 active:bg-blue-800'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 active:bg-gray-100'
              }`}>
              {actionButton.label}
            </button>
          )}
          {cancelButton && (
            <button
              onClick={cancelButton.onClick}
              className="w-full sm:w-auto px-3 sm:px-4 py-2.5 sm:py-2 border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg bg-white hover:bg-gray-50 active:bg-gray-100 min-h-[44px] sm:min-h-0">
              {cancelButton.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default HelperBanner;
